const API_BASE_URL = 'https://astro-jap-backend.vercel.app/api';

// Expose for chat.js login flow
window.AstroAPIBase = API_BASE_URL;

const AstroAPI = {
  async getAstrologers(forceRefresh = false) {
    const CACHE_KEY = 'astro_list_cache';
    const TIME_KEY = 'astro_list_time';
    const TTL = 2 * 60 * 1000; // 2 minutes in-memory/session cache

    if (!forceRefresh) {
      try {
        const cached = sessionStorage.getItem(CACHE_KEY);
        const time = sessionStorage.getItem(TIME_KEY);
        if (cached && time && (Date.now() - parseInt(time, 10) < TTL)) {
          return JSON.parse(cached);
        }
      } catch (e) {}
    }

    const response = await fetch(`${API_BASE_URL}/astrologers`);
    if (!response.ok) throw new Error('Failed to fetch astrologers');
    const data = await response.json();

    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
      sessionStorage.setItem(TIME_KEY, Date.now().toString());
    } catch (e) {}

    return data;
  },

  async getActiveSession(customerId) {
    if (!customerId) return null;
    try {
      const response = await fetch(`${API_BASE_URL}/sessions/get-session`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ customer_id: customerId })
      });
      if (!response.ok) return null;
      const data = await response.json();
      if (!data || !data.id || data.status !== 'active') return null;

      const now = new Date();
      // Ensure session is strictly within valid timeframe
      if (data.end_time) {
        if (new Date(data.end_time) <= now) return null;
      } else if (data.start_time) {
        const expectedEnd = new Date(new Date(data.start_time).getTime() + (data.duration_minutes || 20) * 60000);
        if (expectedEnd <= now) return null;
      } else {
        // If session was created but never started, expire after duration + 15 mins
        const createdAt = data.created_at ? new Date(data.created_at) : (data.scheduled_at ? new Date(data.scheduled_at) : null);
        if (createdAt) {
          const maxLifetime = ((data.duration_minutes || 20) + 15) * 60 * 1000;
          if ((now - createdAt) > maxLifetime) return null;
        }
      }
      return data;
    } catch (e) {
      return null;
    }
  },

  async getSessionById(sessionId) {
    const response = await fetch(`${API_BASE_URL}/sessions/get-session-by-id`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ session_id: sessionId })
    });
    if (!response.ok) throw new Error('Failed to fetch session details');
    return response.json();
  },

  async getCustomerSessions(customerId) {
    const response = await fetch(`${API_BASE_URL}/sessions/get-customer-sessions`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ customer_id: customerId })
    });
    if (!response.ok) throw new Error('Failed to fetch session history');
    return response.json();
  },

  async startSession(sessionId) {
    const response = await fetch(`${API_BASE_URL}/sessions/start-session`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ session_id: sessionId })
    });
    if (!response.ok) throw new Error('Failed to start session');
    return response.json();
  },

  async validateSession(sessionId) {
    const response = await fetch(`${API_BASE_URL}/sessions/validate-session`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ session_id: sessionId })
    });
    if (!response.ok) throw new Error('Failed to validate session');
    return response.json();
  },

  async endSession(sessionId, userType = 'system') {
    const response = await fetch(`${API_BASE_URL}/sessions/end-session`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ session_id: sessionId, user_type: userType })
    });
    return response.json();
  },

  async rejoinSession(sessionId, userType) {
    const response = await fetch(`${API_BASE_URL}/sessions/rejoin-session`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ session_id: sessionId, user_type: userType })
    });
    return response.json();
  },

  async cancelSession(sessionId) {
    const response = await fetch(`${API_BASE_URL}/sessions/cancel-session`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ session_id: sessionId })
    });
    return response.json();
  },

  // ─── Coupons & Offers ────────────────────────────────────────────────────────

  async getCoupons() {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2500);
      const response = await fetch(`${API_BASE_URL}/coupons`, { signal: controller.signal });
      clearTimeout(timeoutId);
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) return data;
        if (data && Array.isArray(data.coupons) && data.coupons.length > 0) return data.coupons;
      }
    } catch (e) {
      console.info('Backend coupons endpoint not active, using verified active store coupons:', e.message);
    }
    // Verified Active Store Coupons fallback
    return [
      {
        code: 'ASTRO100',
        title: '₹100 OFF on Special Rakhi',
        description: 'Get ₹100.00 off on Special Rakhi products (No min. order)',
        discount_type: 'fixed',
        discount_value: 100,
        min_order: 0,
        collections: ['Special Rakhi', 'Rakhi'],
        badge: 'SPECIAL RAKHI'
      },
      {
        code: 'ASTRO50',
        title: 'Flat ₹50 OFF',
        description: 'Get flat ₹50 discount on orders above ₹499',
        discount_type: 'fixed',
        discount_value: 50,
        min_order: 499,
        badge: 'POPULAR'
      },
      {
        code: 'WELCOME10',
        title: '10% Instant OFF',
        description: 'Special 10% discount for all customers',
        discount_type: 'percentage',
        discount_value: 10,
        min_order: 299,
        max_discount: 150,
        badge: 'NEW USER'
      },
      {
        code: 'RAKHI100',
        title: 'Flat ₹100 OFF',
        description: 'Special savings on Rakhi orders above ₹999',
        discount_type: 'fixed',
        discount_value: 100,
        min_order: 999,
        collections: ['Special Rakhi', 'Rakhi'],
        badge: 'FESTIVAL'
      },
      {
        code: 'DEVOTION15',
        title: '15% OFF',
        description: '15% discount on Malas, Gemstones & Yantras',
        discount_type: 'percentage',
        discount_value: 15,
        min_order: 799,
        max_discount: 300,
        collections: ['Malas', 'Gemstones', 'Yantras', 'Rudraksha'],
        badge: 'SPECIAL'
      },
      {
        code: 'ASTROJAP',
        title: 'Flat ₹200 OFF',
        description: 'Mega savings on orders above ₹1499',
        discount_type: 'fixed',
        discount_value: 200,
        min_order: 1499,
        badge: 'MEGA SAVER'
      }
    ];
  },

  async getCouponsCount() {
    try {
      const coupons = await this.getCoupons();
      return { count: coupons ? coupons.length : 0 };
    } catch (e) {
      return { count: 5 };
    }
  },

  async validateCoupon(code, cartTotal) {
    if (!code || typeof code !== 'string') {
      return { valid: false, message: 'Please enter a valid coupon code.' };
    }
    const cleanCode = code.trim().toUpperCase();
    const coupons = await this.getCoupons();
    const coupon = coupons.find(c => c.code.toUpperCase() === cleanCode);

    if (!coupon) {
      return { 
        valid: false, 
        message: `Coupon code "${cleanCode}" is invalid or expired.` 
      };
    }

    if (coupon.min_order && cartTotal < coupon.min_order) {
      const shortAmount = Math.ceil(coupon.min_order - cartTotal);
      return { 
        valid: false, 
        coupon,
        error: 'MIN_ORDER_NOT_MET',
        min_order: coupon.min_order,
        short_amount: shortAmount,
        message: `Add ₹${shortAmount} more to cart to use code "${coupon.code}" (Min order: ₹${coupon.min_order}).` 
      };
    }

    let discountAmount = 0;
    if (coupon.discount_type === 'percentage') {
      discountAmount = Math.round((cartTotal * coupon.discount_value) / 100);
      if (coupon.max_discount && discountAmount > coupon.max_discount) {
        discountAmount = coupon.max_discount;
      }
    } else {
      discountAmount = Math.min(cartTotal, coupon.discount_value);
    }

    return {
      valid: true,
      coupon,
      discountAmount,
      message: `🎉 Coupon "${coupon.code}" applied! You saved ₹${discountAmount}.`
    };
  },

  async getChatMessages(sessionId) {
    const response = await fetch(`${API_BASE_URL}/sessions/messages`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ session_id: sessionId })
    });
    if (!response.ok) throw new Error('Failed to fetch messages');
    return response.json();
  },

  async sendChatMessage(sessionId, senderId, senderType, text, isSystem = false) {
    const response = await fetch(`${API_BASE_URL}/sessions/send-message`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        session_id: sessionId,
        sender_id: senderId,
        sender_type: senderType,
        text: text,
        is_system: isSystem
      })
    });
    if (!response.ok) throw new Error('Failed to send message');
    return response.json();
  },

  async getWalletBalance(customerId) {
    const response = await fetch(`${API_BASE_URL}/wallet/balance`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ customer_id: customerId })
    });
    if (!response.ok) throw new Error('Failed to fetch wallet balance');
    return response.json();
  },

  async calculateCashbackUsage(customerId, cartTotal) {
    const response = await fetch(`${API_BASE_URL}/wallet/calculate-usage`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ customer_id: customerId, cart_total: cartTotal })
    });
    if (!response.ok) throw new Error('Failed to calculate cashback');
    return response.json();
  },

  async debitWallet(customerId, amount, description, referenceId) {
    const response = await fetch(`${API_BASE_URL}/wallet/debit`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        customer_id:  customerId,
        amount:       amount,
        description:  description,
        reference_id: referenceId
      })
    });
    return response.json();
  },

  async manualRecharge(customerId, amount) {
    const response = await fetch(`${API_BASE_URL}/wallet/manual-credit`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ customer_id: customerId, amount: amount })
    });
    return response.json();
  },

  async createManualSession(customerId, customerName, astrologerId, duration) {
    const response = await fetch(`${API_BASE_URL}/sessions/create-manual`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        customer_id:   customerId,
        customer_name: customerName,
        astrologer_id: astrologerId,
        duration:      duration
      })
    });
    // We don't throw error here to handle 402 Insufficient Balance specially in UI
    return response;
  },

  // ─── Slots ──────────────────────────────────────────────────────────────────

  async getAvailableSlots(astroId, date, duration) {
    let url = `${API_BASE_URL}/slots/${astroId}`;
    const params = new URLSearchParams();
    if (date)     params.set('date', date);
    if (duration) params.set('duration', duration);
    if (params.toString()) url += '?' + params.toString();
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch slots');
    return response.json();
  },

  async bookSlot(slotId, customerId, customerName, astrologerId, durationMin) {
    return fetch(`${API_BASE_URL}/slots/book`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        slot_id:       slotId,
        customer_id:   customerId,
        customer_name: customerName,
        astrologer_id: astrologerId,
        duration_min:  durationMin
      })
    });
  },

  async getCustomerBookings(customerId) {
    const response = await fetch(`${API_BASE_URL}/slots/booked/${customerId}`);
    if (!response.ok) throw new Error('Failed to fetch bookings');
    return response.json();
  },

  async generateSlots(astrologerId, slots) {
    const response = await fetch(`${API_BASE_URL}/slots/generate`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ astrologer_id: astrologerId, slots })
    });
    return response;
  },

  // ─── Astrologer Dashboard ────────────────────────────────────────────────────

  async getDashboardStats(astrologerId) {
    const response = await fetch(`${API_BASE_URL}/astrologers/dashboard-stats/${astrologerId}`);
    if (!response.ok) throw new Error('Failed to fetch dashboard stats');
    return response.json();
  },

  async getUpcomingSessions(astrologerId) {
    const response = await fetch(`${API_BASE_URL}/astrologers/upcoming-sessions/${astrologerId}`);
    if (!response.ok) throw new Error('Failed to fetch upcoming sessions');
    return response.json();
  },

  async getSessionHistory(astrologerId, page = 1) {
    const response = await fetch(`${API_BASE_URL}/astrologers/session-history/${astrologerId}?page=${page}`);
    if (!response.ok) throw new Error('Failed to fetch session history');
    return response.json();
  },

  async updatePricing(astrologerId, price20, price60) {
    return fetch(`${API_BASE_URL}/astrologers/update-pricing`, {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ astrologer_id: astrologerId, price_20_min: price20, price_60_min: price60 })
    });
  },

  async toggleAvailability(astrologerId) {
    return fetch(`${API_BASE_URL}/astrologers/toggle-availability`, {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ astrologer_id: astrologerId })
    });
  },

  async getAstrologerPricing(astrologerId) {
    const response = await fetch(`${API_BASE_URL}/astrologers/${astrologerId}/pricing`);
    if (!response.ok) throw new Error('Failed to fetch pricing');
    return response.json();
  },

  // ─── Payouts ─────────────────────────────────────────────────────────────────

  async requestPayout(astrologerId, amount) {
    return fetch(`${API_BASE_URL}/payouts/request`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ astrologer_id: astrologerId, amount })
    });
  },

  async getPayoutHistory(astrologerId) {
    const response = await fetch(`${API_BASE_URL}/payouts/history/${astrologerId}`);
    if (!response.ok) throw new Error('Failed to fetch payout history');
    return response.json();
  },

  // ─── Reviews ─────────────────────────────────────────────────────────────────

  async submitReview(sessionId, customerId, rating, comment) {
    return fetch(`${API_BASE_URL}/reviews/submit`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ session_id: sessionId, customer_id: customerId, rating, comment })
    });
  },

  async getSessionReview(sessionId) {
    const response = await fetch(`${API_BASE_URL}/reviews/session/${sessionId}`);
    if (!response.ok) throw new Error('Failed to fetch review');
    return response.json();
  }
};

window.AstroAPI = AstroAPI;

